import React, {useEffect} from 'react';

import {AdvancedSearchType} from 'src/openapi/admin/models/AdvancedSearchType';
import {DateRangeFilter} from 'src/openapi/admin/models/DateRangeFilter';
import {SavedListType} from 'src/openapi/admin/models/SavedListType';
import {SavedSearchRequestSortField} from 'src/openapi/admin/models/SavedSearchRequest';
import {SortDirection as OpenApiSortDirection} from 'src/openapi/admin/models/SortDirection';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {SortHeaderFilter} from '@pb/kit-component-filters';
import {SortDirections} from '@pb/kit-component-sort-header';
import {Cell, Head} from '@pb/kit-component-table';

import {SORT_COLUMNS} from '../../constants';
import {SavedSearchType, SortDirectionType} from '../../typedef';
import {buildSelectedValues, getDirection, getSavedType, getTypeCaption, getTypeOptions} from '../../utils/util';
import {DateColumnFilter} from '../date-column-filter';

import {useDateFilter} from './hooks/useDateFilter';
import {useSort} from './hooks/useSort';
import {useTypeFilter} from './hooks/useTypeFilter';


export type TableHeadersProps = {
    savedSearchType: SavedSearchType,
    onSortChange?: (field: SavedSearchRequestSortField, direction: OpenApiSortDirection) => void,
    onTypeFilterChange?: (filter: AdvancedSearchType[]) => void,
    onListTypeFilterChange?: (filter: SavedListType[]) => void,
    onDateFilterChange?: (field: 'createDate' | 'lastRun' | 'lastShared', filter: DateRangeFilter | undefined) => void,
};

export const TableHeaders = ({
    savedSearchType,
    onSortChange,
    onTypeFilterChange,
    onListTypeFilterChange,
    onDateFilterChange,
}: TableHeadersProps) => {

    const {
        typeSortDirection,
        dateCreatedSortDirection,
        lastRunSortDirection,
        lastSharedSortDirection,
        lastSortedColumn,
        handleSort,
    } = useSort({onSortChange});

    const {
        typeFilterValue,
        isTypeFilterActive,
        selectedTypeValue,
        handleApplyTypeFilter,
        handleTypeSelection,
        handleResetTypeFilter,
        handleCancelTypeFilter,
    } = useTypeFilter({
        savedSearchType,
        onTypeFilterChange,
        onListTypeFilterChange,
    });

    const {dateFilterState: dateCreatedFilter, setSortDirection: setDateCreatedSortDirection} = useDateFilter({
        onDateFilterChange,
        field: 'createDate',
    });

    const {dateFilterState: lastRunFilter, setSortDirection: setLastRunSortDirection} = useDateFilter({
        onDateFilterChange,
        field: 'lastRun',
    });

    const {dateFilterState: lastSharedFilter, setSortDirection: setLastSharedSortDirection} = useDateFilter({
        onDateFilterChange,
        field: 'lastShared',
    });

    // Update sort directions when they change
    useEffect(() => {
        setDateCreatedSortDirection(dateCreatedSortDirection);
    }, [dateCreatedSortDirection, setDateCreatedSortDirection]);

    useEffect(() => {
        setLastRunSortDirection(lastRunSortDirection);
    }, [lastRunSortDirection, setLastRunSortDirection]);

    useEffect(() => {
        setLastSharedSortDirection(lastSharedSortDirection);
    }, [lastSharedSortDirection, setLastSharedSortDirection]);

    const sortedTypeOptions = Array.from(new Set(getTypeOptions(savedSearchType)))
        .sort((a, b) => getTypeCaption(a).localeCompare(getTypeCaption(b)));

    const typeComboboxItems = sortedTypeOptions.map(code => (
        <ComboboxItem
            key={String(code)}
            caption={getTypeCaption(code)}
            value={code}
        />
    ));

    const sortDirections = {
        [SORT_COLUMNS.TYPE]: typeSortDirection,
        [SORT_COLUMNS.DATE_CREATED]: dateCreatedFilter.sortDirection,
        [SORT_COLUMNS.LAST_RUN]: lastRunFilter.sortDirection,
        [SORT_COLUMNS.LAST_SHARED]: lastSharedFilter.sortDirection,
    } as const;

    const typeDirection = getDirection(SORT_COLUMNS.TYPE, lastSortedColumn, sortDirections);
    const dateCreatedDirection = getDirection(SORT_COLUMNS.DATE_CREATED, lastSortedColumn, sortDirections);
    const lastRunDirection = getDirection(SORT_COLUMNS.LAST_RUN, lastSortedColumn, sortDirections);
    const lastSharedDirection = getDirection(SORT_COLUMNS.LAST_SHARED, lastSortedColumn, sortDirections);

    const onTypeSort = (dir: SortDirectionType) => handleSort(SORT_COLUMNS.TYPE, dir);
    const onDateCreatedSort = (dir: SortDirectionType) => handleSort(SORT_COLUMNS.DATE_CREATED, dir);
    const onLastRunSort = (dir: SortDirectionType) => handleSort(SORT_COLUMNS.LAST_RUN, dir);
    const onLastSharedSort = (dir: SortDirectionType) => handleSort(SORT_COLUMNS.LAST_SHARED, dir);

    return (
        <Head>
            <Cell width="600px" className="nowrap">
                Name
            </Cell>

            <Cell width="11rem">
                <SortHeaderFilter
                    caption={`${getSavedType(savedSearchType)} Type`}
                    sortable={true}
                    direction={typeDirection}
                    defaultDirection={SortDirections.ASC}
                    onSort={onTypeSort}
                    onApply={() => handleApplyTypeFilter(
                        {...typeFilterValue, values: buildSelectedValues(selectedTypeValue)})}
                    onReset={handleResetTypeFilter}
                    onCancel={handleCancelTypeFilter}
                    filterActive={isTypeFilterActive}>
                    <ComboBox
                        value={selectedTypeValue}
                        onChange={handleTypeSelection}
                        placeholder={`Select ${getSavedType(savedSearchType)} Type`}
                        multiselectable={true}>
                        {typeComboboxItems}
                    </ComboBox>
                </SortHeaderFilter>
            </Cell>

            <Cell width="11rem">
                <DateColumnFilter
                    caption="Date Created"
                    idPrefix="date-created-filter"
                    startDate={dateCreatedFilter.startDate}
                    endDate={dateCreatedFilter.endDate}
                    direction={dateCreatedDirection}
                    defaultDirection={SortDirections.DESC}
                    filterActive={dateCreatedFilter.isActive}
                    onSort={onDateCreatedSort}
                    onApply={dateCreatedFilter.handlers.onApply}
                    onReset={dateCreatedFilter.handlers.onReset}
                    onCancel={dateCreatedFilter.handlers.onCancel}
                    onStartChange={dateCreatedFilter.handlers.onStartChange}
                    onEndChange={dateCreatedFilter.handlers.onEndChange}
                />
            </Cell>

            <Cell width="9rem">
                <DateColumnFilter
                    caption="Last Run"
                    idPrefix="last-run-filter"
                    startDate={lastRunFilter.startDate}
                    endDate={lastRunFilter.endDate}
                    direction={lastRunDirection}
                    defaultDirection={SortDirections.DESC}
                    filterActive={lastRunFilter.isActive}
                    onSort={onLastRunSort}
                    onApply={lastRunFilter.handlers.onApply}
                    onReset={lastRunFilter.handlers.onReset}
                    onCancel={lastRunFilter.handlers.onCancel}
                    onStartChange={lastRunFilter.handlers.onStartChange}
                    onEndChange={lastRunFilter.handlers.onEndChange}
                />
            </Cell>

            <Cell width="11rem">
                <DateColumnFilter
                    caption="Last Shared"
                    idPrefix="last-shared-filter"
                    startDate={lastSharedFilter.startDate}
                    endDate={lastSharedFilter.endDate}
                    direction={lastSharedDirection}
                    defaultDirection={SortDirections.DESC}
                    filterActive={lastSharedFilter.isActive}
                    onSort={onLastSharedSort}
                    onApply={lastSharedFilter.handlers.onApply}
                    onReset={lastSharedFilter.handlers.onReset}
                    onCancel={lastSharedFilter.handlers.onCancel}
                    onStartChange={lastSharedFilter.handlers.onStartChange}
                    onEndChange={lastSharedFilter.handlers.onEndChange}
                />
            </Cell>

            <Cell width="9rem" className="nowrap">
                Last Modified
            </Cell>

            <Cell>
                Alerts
            </Cell>
            <Cell>
                # of Results
            </Cell>
            <Cell>
                # of Alerts
            </Cell>
        </Head>
    );
};
